"use client";

import { RatingStars } from "@/components/rating-display";
import { Separator } from "@/components/shadcn/separator";
import { useSession } from "@/lib/auth/client";
import { FetchedSeriesRating } from "@/types";
import axios from "axios";
import { HTMLAttributes, useEffect, useState } from "react";
import { SeriesAction } from "./series-action";

interface SeriesRatingProps {
    seriesId: number;
}

export default function SeriesRating({
    seriesId,
    className,
    ...props
}: HTMLAttributes<HTMLDivElement> & SeriesRatingProps) {
    const { data: session } = useSession();

    const [ratings, setRatings] = useState<FetchedSeriesRating>({
        avg: null,
        count: null,
        userRating: null,
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        async function fetchData() {
            try {
                const response = await axios.get(`/api/series/rating`, {
                    params: { seriesId: seriesId },
                });
                setRatings(response?.data);
            } catch (error) {
                console.error("Ratings fetch error:", error);
            }
        }

        fetchData();
    }, [seriesId]);

    async function handleRating(value: number) {
        if (!session?.user) return;

        try {
            setLoading(true);
            const { data: ratingResponse } = await axios.post(
                "/api/series/rating",
                { value, seriesId },
                {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                },
            );

            setRatings(ratingResponse);
        } catch (error) {
            console.error("Rating error:", error);
        } finally {
            setLoading(false);
        }
    }

    const canRate = Boolean(session?.user) && !loading;
    const formattedRating = ratings.avg === null ? "-" : ratings.avg.toFixed(2);
    const formattedCount =
        ratings.count !== null && ratings.count > 0
            ? `(${Math.round(ratings.count)})`
            : null;

    return (
        <SeriesAction className={className} {...props}>
            <RatingStars
                fill={ratings.avg ?? 0}
                onClick={canRate ? handleRating : undefined}
                disabled={!canRate}
            />
            <Separator
                orientation="vertical"
                className="h-auto! self-stretch"
            />
            <span className="flex items-center gap-0.5">
                <span className="text-foreground font-medium">
                    {formattedRating}
                </span>
                <span className="text-muted-foreground">/5</span>
                {formattedCount && (
                    <span className="text-muted-foreground">
                        {formattedCount}
                    </span>
                )}
            </span>
        </SeriesAction>
    );
}
